import { Dialog, Label, Select } from "@win-reality/react-ui";
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useMemo, useState } from "react";

import type { IPlayer } from "../store/types";

type PlayerSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (player: IPlayer) => void;
  /** The team's roster to choose from */
  roster: IPlayer[];
  type: "pitcher" | "catcher";
};

/**
 * PlayerSelectModal - Dialog for selecting a pitcher or catcher from the roster
 *
 * Uses react-ui Dialog with DialogScrollableContent and DialogFooter for consistent layout.
 */
export const PlayerSelectModal = ({
  isOpen,
  onClose,
  onSubmit,
  roster,
  type,
}: PlayerSelectModalProps) => {
  const [selectedPlayerUuid, setSelectedPlayerUuid] = useState<string | null>(
    null,
  );

  // Format roster for Select component
  const playerOptions = useMemo(
    () =>
      roster.map((player) => ({
        value: player.uuid,
        label: `${player.lastName}, ${player.firstName}`,
      })),
    [roster],
  );

  const handleConfirm = () => {
    if (!selectedPlayerUuid) return;

    const player = roster.find((p) => p.uuid === selectedPlayerUuid);
    if (player) {
      onSubmit(player);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedPlayerUuid(null);
    onClose();
  };

  const title = type === "pitcher" ? "Select Pitcher" : "Select Catcher";
  const labelText = type === "pitcher" ? "Choose pitcher" : "Choose catcher";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-4">
              <div>
                <Label className="mb-2 block text-sm font-medium">
                  {labelText}
                </Label>
                <Select
                  value={selectedPlayerUuid ?? ""}
                  onValueChange={(value) =>
                    setSelectedPlayerUuid(String(value))
                  }
                  placeholder="Select a player..."
                  items={playerOptions}
                />
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                onClick={handleConfirm}
                isDisabled={!selectedPlayerUuid}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
